import React, { useEffect, useState, useCallback } from 'react';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import styled, { css } from 'styled-components';
import { addDays, compareAsc, subDays, isFirstDayOfMonth, isLastDayOfMonth } from 'date-fns';
import ru from 'date-fns/locale/ru';

import { Landing } from '@lt/components/icons-react/userflow';
import { Flight } from '@lt/components/icons-react';

import { datepickerName, rightArrowSymbol } from '../../constants';
import ApplyButton from '../ApplyButton';
import { convertDatesToStringWithEllipsis } from '../../../../utils';
import { MAXIMUM_NIGHTS, MAXIMUM_SHOW_DAYS, MONTH_COUNT, MONTH_COUNT_MOBILE } from './constants';
import renderDayContents from './components/renderDayContens';
import renderCustomHeader from './components/renderCustomHeader';
import NightsInfo from './components/NightsInfo';
import { CalendarProps } from './types';
import CloseButton from '../../../CloseButton';

registerLocale('ru', ru);

export const Calendar = ({
  open,
  startDate,
  endDate,
  closeCalendar,
  onChange,
  startShift,
  endShift,
  changeShift,
  applyButtonHandler,
  minDate,
  excludeDates,
  isStartDateFocus,
  isEndDateFocus,
  isMobile = false,
  isWizard = false,
  isFlexDatesOff = false,
  isHotelOnly,
}: CalendarProps) => {
  const [isAutoSwitchMonthRight, setIsAutoSwitchMonthRight] = useState(true);
  const [isAutoSwitchMonthLeft, setIsAutoSwitchMonthLeft] = useState(true);

  const calcMinDate = useCallback(() => {
    if (endDate && minDate && isStartDateFocus) {
      const minimumDateConsideringEndDate = subDays(endDate, MAXIMUM_NIGHTS);
      if (compareAsc(new Date(minimumDateConsideringEndDate), new Date(minDate)) > 0) {
        return minimumDateConsideringEndDate;
      }
    }
    return minDate;
  }, [endDate, minDate, isStartDateFocus]);

  const calcMaxDate = useCallback(() => {
    const maximumShowDays = addDays(new Date(), MAXIMUM_SHOW_DAYS);
    if (startDate && isEndDateFocus) {
      const maximumNights = addDays(startDate, MAXIMUM_NIGHTS);
      if (compareAsc(new Date(maximumNights), new Date(maximumShowDays)) < 0) {
        return maximumNights;
      }
    }
    return maximumShowDays;
  }, [startDate, isEndDateFocus]);

  const isIncreaseMonth =
    (endDate && isLastDayOfMonth(endDate) && isAutoSwitchMonthRight && endShift.right === 1) ||
    false;

  const isDecreaseMonth =
    (startDate && isFirstDayOfMonth(startDate) && isAutoSwitchMonthLeft && startShift.left === 1) ||
    false;

  useEffect(() => {
    if (endShift.right === 0) {
      setIsAutoSwitchMonthRight(true);
    }
  }, [endShift.right]);

  useEffect(() => {
    if (startShift.left === 0) {
      setIsAutoSwitchMonthLeft(true);
    }
  }, [startShift.left]);

  if (!open) return null;

  return (
    <StyledWrapper $isMobile={isMobile} $isWizard={isWizard} $isFlexDatesOff={isFlexDatesOff}>
      {!isMobile && <CalendarMask onClick={closeCalendar} />}
      {isMobile && !isWizard && (
        <Header>
          <StyledCloseButton onClick={closeCalendar} />
        </Header>
      )}
      <ReactDatePicker
        locale="ru"
        startDate={startDate}
        endDate={endDate}
        minDate={calcMinDate()}
        showPopperArrow={false}
        onChange={onChange}
        selectsStart={isStartDateFocus}
        selectsEnd={isEndDateFocus}
        renderDayContents={renderDayContents({
          startDate,
          endDate,
          startShift,
          endShift,
          changeShift,
          isMobile,
          minDate,
          excludeDates,
          isFlexDatesOff,
        })}
        disabledKeyboardNavigation
        maxDate={calcMaxDate()}
        // нет в @types/react-datepicker
        /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
        /* @ts-ignore */
        selectsDisabledDaysInRange
        inline
        monthsShown={isMobile ? MONTH_COUNT_MOBILE : MONTH_COUNT}
        renderCustomHeader={renderCustomHeader({
          startDate,
          endDate,
          isDecreaseMonth,
          isIncreaseMonth,
          isMobile,
          setIsAutoSwitchMonthRight,
          setIsAutoSwitchMonthLeft,
        })}
      >
        <Footer $isMobile={isMobile}>
          {startDate && endDate && (
            <>
              <ApplyButton onClick={applyButtonHandler}>
                <DatesText>
                  {!isHotelOnly ? <PlaneIcon /> : null}
                  {convertDatesToStringWithEllipsis({
                    date: startDate,
                    shift: startShift,
                  })}
                  <SplitArrowContainer>{rightArrowSymbol}</SplitArrowContainer>
                  {!isHotelOnly ? <LandingIcon /> : null}
                  {convertDatesToStringWithEllipsis({
                    date: endDate,
                    shift: endShift,
                  })}
                </DatesText>
              </ApplyButton>
              <NightsInfo
                startDate={startDate}
                endDate={endDate}
                startShift={startShift}
                endShift={endShift}
                isMobile={isMobile}
              />
            </>
          )}
        </Footer>
      </ReactDatePicker>
    </StyledWrapper>
  );
};

const StyledWrapper = styled.div<{
  $isMobile: boolean;
  $isWizard?: boolean;
  $isFlexDatesOff?: boolean;
}>`
  height: 100%;

  .${datepickerName} {
    padding: ${({ $isMobile, $isWizard }) =>
      $isMobile && !$isWizard ? '72px 0 64px' : '16px 0 64px'};
    font-family: 'Backpack', Helvetica, sans-serif;
    border: none;
    display: flex;
    flex-direction: column;
    width: 100%;
    align-items: center;
    background-color: #fff;

    ${({ $isMobile, $isWizard }) =>
      $isMobile &&
      !$isWizard &&
      css`
        top: 60px;
        position: fixed;
        left: 0;
        z-index: 120;
        height: 100vh;
      `}

    ${({ $isWizard }) =>
      $isWizard &&
      css`
        height: 100vh;
      `}

    // desktop position
    ${({ $isMobile, $isFlexDatesOff }) =>
      !$isMobile &&
      css`
        display: inline-block;
        position: absolute;
        width: 744px;
        left: 50%;
        transform: translate(${$isFlexDatesOff ? '-82%' : '-50%'}, 0);
        z-index: 999;
        padding-bottom: 16px;
        box-shadow: 0 2px 4px rgb(210 208 197 / 80%);
      `};

    &__header {
      background-color: #fff;
      border: none;
      padding: 0;
    }

    &__current-month {
      font-size: 18px;
      line-height: 22px;
      color: #17181a;
      text-transform: capitalize;
      border: none;
    }

    &__day-name {
      color: #c2c5cc;

      &:nth-last-child(-n + 2) {
        color: #efc1c7;
      }

      &s {
        text-transform: uppercase;
        display: flex;
        justify-content: space-around;
        padding: 0 16px;
      }
    }

    &__month {
      padding: 8px 16px 16px;
      margin: 0;
    }

    &__week {
      display: flex;
    }

    &__day {
      width: ${({ $isMobile }) => `${$isMobile ? 44 : 48}px`};
      height: ${({ $isMobile }) => `${$isMobile ? 44 : 48}px`};
      margin: 0;
      border-radius: 0;
      font-size: 18px;
      line-height: 22px;
      color: #4d4d4d;

      &--disabled {
        color: #c2c5cc;
      }

      &--in-range {
        background-color: #f3f2e8;
        color: #4d4d4d;

        &:hover {
          background-color: #f3f2e8;
        }
      }

      &--in-selecting-range {
        background-color: #e6f2ff;

        &:not(.${datepickerName}__day--in-range) {
          background-color: #e6f2ff;
        }
      }

      &--range-start,
      &--range-end {
        color: #17181a;
        font-weight: bold;
        position: relative;

        &:before {
          position: absolute;
          left: 0;
          top: -12px;
          font-size: 9px;
          line-height: 12px;
          font-weight: normal;
          padding: 0 4px;
          border-radius: 4px 4px 0 0;
        }
      }

      &--range-start {
        background-color: #fffbe5;

        &:before {
          content: 'Туда';
          color: #17181a;
          background-color: #f8e71c;
        }
      }

      &--range-end {
        background-color: #cce763;
        border-color: #6abf40;

        &:before {
          content: 'Обратно';
          color: #fff;
          background-color: #6abf40;
        }
      }
      &.${datepickerName}__day--outside-month {
        * {
          display: none;
        }
        background-color: transparent;
        border: none;
        color: transparent;

        &:before {
          content: none;
        }
      }
    }
  }

  ${({ $isMobile, $isWizard }) =>
    $isMobile &&
    css`
      ${!$isWizard && `width: 100%;`}
      display: flex;
      justify-content: center;
      overflow-y: scroll;
    `}
`;

const Footer = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  clear: both;

  & > * + * {
    margin-top: 8px;
  }

  ${({ $isMobile }) =>
    $isMobile &&
    css`
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      padding: 16px 8px;
      background: #fff;
      width: 100%;
      z-index: 1;
      flex-direction: column-reverse;
    `};
`;

const SplitArrowContainer = styled.span`
  font-family: cursive;
  padding: 0 4px;
`;

const LandingIcon = styled(Landing)`
  stroke: #fff;
  width: 20px;
  height: 20px;
  align-self: end;
`;

const PlaneIcon = styled(Flight)`
  stroke: #fff;
  width: 16px;
  height: 16px;
  margin-right: 4px;
`;

const DatesText = styled.div`
  display: flex;
  align-items: center;
`;

const CalendarMask = styled.div`
  height: 100vh;
  width: 100%;
  left: 0;
  top: 0;
  overflow: hidden;
  position: fixed;
  z-index: 2;
`;

// @todo
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const StyledCloseButton = styled(CloseButton).attrs({ $inMobileInput: true })`
  position: fixed;
  top: 20px;
  margin: 0 12px 0 0;
`;

const Header = styled.div`
  position: fixed;
  height: 64px;
  background: #f5f4f2;
  width: 100%;
  margin-bottom: 16px;
  z-index: 120;
  top: 0;

  &:after {
    content: '';
    position: fixed;
    top: 56px;
    height: 8px;
    width: 100%;
    background: #fff;
    border-radius: 8px 8px 0 0;
  }
`;
